import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import analytics, {
  normalizeRoutePath,
  readStoredAnalyticsConsent,
} from '../services/analytics';
import { useAuth } from './AuthContext';
import { trackPageView as trackMetaPageView } from '../services/metaPixel';
import { isStandalonePwa } from '../utils/pwaDisplayMode';
import { decidePageViewBeacon, referrerDomain } from '../utils/pageViewBeacon';
import { discardEngagement, trackEngagementPage } from '../utils/engagementBeacon';
import { isIndexHtmlPath } from '../utils/indexHtmlUrl';
import {
  ENTERPRISE_PAY_TOKEN_ROUTE,
  isTokenBearingPath,
  maskTokenPath,
} from '../utils/tokenPathMask';
// Import only the SHA-256 algorithm (the sole crypto-js function used here)
// instead of the full library — keeps ~180 KB of unused ciphers/hashers out of
// the eager app bundle while producing byte-identical output.
import sha256 from 'crypto-js/sha256';

/** sessionStorage flag: the `pwa_standalone_launch` event is once per session. */
const STANDALONE_LAUNCH_KEY = 'analytics_pwa_standalone_launch';

/** Short-circuit for this document once the event is known to be reported. */
let standaloneLaunchSettled = false;

/**
 * Report `pwa_standalone_launch` at most once per session, and ONLY once it has
 * actually been queued.
 *
 * THE ORDER IS THE WHOLE POINT. Writing the sessionStorage flag before calling
 * trackEvent burned it on an event that consent had held back, so a user who
 * launched the installed app and accepted the cookie banner a moment later lost
 * the launch for the rest of the session - permanently, since the flag says it
 * was reported. `trackEvent` returns whether the event entered the queue, so
 * the flag is written only on a real success and the attempt simply repeats
 * (mount, consent change, next navigation) until one lands.
 *
 * Every storage access is guarded: Safari private mode throws on sessionStorage.
 */
const attemptStandaloneLaunchEvent = () => {
  if (standaloneLaunchSettled) return;

  try {
    if (sessionStorage.getItem(STANDALONE_LAUNCH_KEY)) {
      standaloneLaunchSettled = true;
      return;
    }
  } catch (error) {
    /* unreadable storage: fall through and let the write attempt decide */
  }

  if (!isStandalonePwa()) return;

  if (!analytics.trackEvent('pwa_standalone_launch')) return;

  standaloneLaunchSettled = true;
  try {
    sessionStorage.setItem(STANDALONE_LAUNCH_KEY, '1');
  } catch (error) {
    /* Storage blocked. The in-memory flag still holds for this document, which
       is the case that matters - a second report would need a full reload. */
  }
};

// ---------------------------------------------------------------------------
// Email CTA landing
// ---------------------------------------------------------------------------

/** sessionStorage flag: the `email_cta_landed` event is once per session. */
const EMAIL_CTA_LANDED_KEY = 'analytics_email_cta_landed';

/** Short-circuit for this document once the event is known to be reported. */
let emailCtaLandedSettled = false;

/**
 * The same shape CaptureAcquisition validated on the way IN.
 *
 * Re-validating at the emit site is cheap insurance against a stored value that
 * did not come through that path (a hand-edited sessionStorage, a future
 * writer). The cost of being wrong is not one bad row: `event_params` is
 * validated server-side per BATCH, so one malformed param 422s the whole batch
 * and `disableBatch()` then stands the queue down for the REST OF THE PAGE -
 * every product event after it, from every surface, is silently lost.
 */
const SRC_RE = /^[a-z_]{1,32}$/;

/**
 * Report `email_cta_landed` at most once per session, and ONLY once it has
 * actually been queued.
 *
 * Same retry-until-it-lands idiom as attemptStandaloneLaunchEvent above, for
 * the same reason: `trackEvent` returns false while the consent gate holds a
 * product event back, so banking the sessionStorage flag before that returned
 * true would lose the landing for the whole session - permanently, since the
 * flag then says it was reported.
 *
 * The stored `acq_src` is KEPT after a successful emit rather than cleared: it
 * is session-scoped and harmless, the flag is what makes this idempotent, and a
 * value that survives is available to anything else that wants to know how this
 * visit arrived.
 */
const attemptEmailCtaLandedEvent = () => {
  if (emailCtaLandedSettled) return;

  try {
    if (sessionStorage.getItem(EMAIL_CTA_LANDED_KEY)) {
      emailCtaLandedSettled = true;
      return;
    }
  } catch (error) {
    /* unreadable storage: fall through and let the write attempt decide */
  }

  let src = null;
  try {
    src = sessionStorage.getItem('acq_src');
  } catch (error) {
    /* unreadable storage: nothing to report */
  }

  // No tag on this visit is the common case, and it is not a settled state -
  // the value is written by CaptureAcquisition, which may not have run yet.
  if (typeof src !== 'string' || !SRC_RE.test(src)) return;

  if (!analytics.trackEvent('email_cta_landed', { src })) return;

  emailCtaLandedSettled = true;
  try {
    sessionStorage.setItem(EMAIL_CTA_LANDED_KEY, '1');
  } catch (error) {
    /* Storage blocked. The in-memory flag still holds for this document. */
  }
};

// Create the Analytics Context
const AnalyticsContext = createContext({
  analytics: analytics,
  trackEvent: () => {},
  trackPageView: () => {},
  updateConsent: () => {},
  hasConsent: false
});

// #349: the one shared rule for path-borne secrets (`/enterprise/pay/<token>`).
// Re-exported so existing importers of this context keep one entry point.
export { ENTERPRISE_PAY_TOKEN_ROUTE, maskTokenPath };

// Custom hook to use analytics context
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

// Analytics Provider component
export const AnalyticsProvider = ({ children }) => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [hasConsent, setHasConsent] = useState(false);

  // Initialize analytics on mount
  useEffect(() => {
    // Initialize the analytics service
    analytics.initialize();

    // Check for existing consent in localStorage.
    //
    // Through the SHARED reader, which is the bug this line used to carry: the
    // cookie banner stores the decision at `consent.preferences.analytics` and
    // this file read a top-level `consent.analytics` that nothing has ever
    // written, so `hasConsent` was false for every user who had accepted
    // analytics cookies - and with it every consumer of this context.
    if (readStoredAnalyticsConsent() === true) {
      setHasConsent(true);
      analytics.updateConsent(true);
    }

  }, []);

  // PWA standalone launch - once per session (see attemptStandaloneLaunchEvent).
  //
  // Keyed on `hasConsent` AND `location` rather than running once on mount: the
  // cookie banner grants consent through `window.analytics` directly, which
  // this component never hears about, so the retry has to ride something that
  // does move. Both are cheap - the helper short-circuits on a module flag as
  // soon as the event has landed.
  useEffect(() => {
    attemptStandaloneLaunchEvent();
  }, [hasConsent, location]);

  // Email CTA landing - once per session (see attemptEmailCtaLandedEvent).
  //
  // Keyed on `hasConsent` AND `location` for the same reason the launch event
  // above is: the cookie banner grants consent through `window.analytics`,
  // which this component never hears about, so the retry has to ride something
  // that does move.
  //
  // AUTHENTICATED users only, and this is the extra gate the launch event does
  // not need: the batch endpoint is authed-only, so an emit while the session
  // is still resolving would enter the queue and then 401-drop - burning the
  // one attempt on the arrival this event exists to count. Waiting for
  // `loading` to clear is the same rule app_route_viewed applies.
  useEffect(() => {
    if (loading || !user) return;
    attemptEmailCtaLandedEvent();
  }, [hasConsent, location, user, loading]);

  // Meta Pixel PageView on route change. Gated INSIDE metaPixel (marketing
  // consent + safe-URL check) and deduped per location, so this is a no-op for
  // everyone who has not granted marketing consent. Deliberately NOT keyed on
  // `hasConsent` - that is ANALYTICS consent; the grant path fires its own.
  //
  // NEVER on a token-bearing path. The pixel reports the FULL url as `dl`, and
  // `/enterprise/pay/<token>` would hand Meta the customer's pay-link token.
  // (`metaPixel.js`'s own BLOCKED_PATH_PREFIXES does not list that route yet -
  // this is the belt at the one call site this file owns.)
  //
  // Nor on a `/<route>/index.html` address (#465) - see the GA4 effect below.
  useEffect(() => {
    if (isTokenBearingPath(location.pathname)) return;
    if (isIndexHtmlPath(location.pathname)) return;
    trackMetaPageView();
  }, [location.pathname, location.search]);

  // Track page views on route change (GA4 — consent-gated surface).
  //
  // NOTHING fires on a token-bearing path (#349 R3-03). The pay page moves its
  // token out of the address on arrival, so the clean `/enterprise/pay` that
  // follows a moment later is the view that gets counted - and while the token
  // is still in `document.location`, gtag would put it in `dl` whatever path
  // we hand it. The masking below stays as the second line.
  //
  // Nor on a `/<route>/index.html` address (#465): the static host's 301 target,
  // which no route matches and RedirectIndexHtml is about to replace with the
  // real `/<route>/` - counting it would add a view of a page that does not
  // exist, then count the real one again.
  useEffect(() => {
    if (isTokenBearingPath(location.pathname)) return;
    if (isIndexHtmlPath(location.pathname)) return;
    if (hasConsent && location.pathname) {
      const pageTitle = document.title || 'LeadifyAI';
      // Masked: GA4's page_location is built from this path.
      analytics.trackPageView(maskTokenPath(location.pathname), pageTitle);
    }
  }, [location, hasConsent]);

  // ---------------------------------------------------------------------------
  // In-app route views (product analytics).
  //
  // A SEPARATE effect from the GA4 one above rather than another line inside
  // it: this one depends on `user`, and adding `user` to that effect's deps
  // would re-fire GA4's page_view every time the user object's identity changes
  // on an unchanged route - a double count in the one surface that already
  // counts page views.
  //
  // AUTHENTICATED users only. The batch endpoint is authed, and anonymous
  // traffic is already counted by the first-party page-view beacon below; a
  // logged-out visitor here would just mint rows nobody can attribute.
  //
  // The event carries the route PATTERN, never the concrete path: ids become
  // `:id` and the query string is dropped outright (normalizeRoutePath). The
  // consent gate is NOT re-implemented here - `analytics.trackEvent` applies it
  // for every product event, which is the only place it can be right.
  //
  // De-duplicated on the RAW pathname, not the pattern: a pure query-string
  // change must not fire twice (the pattern is identical, so the two rows would
  // be indistinguishable), while /leads/1 -> /leads/2 is a genuine second view
  // of that route and still counts.
  // ---------------------------------------------------------------------------
  const lastRouteRef = useRef(null);
  useEffect(() => {
    if (loading || !user) return;

    const path = location.pathname;
    // Not on a token-bearing path (#349 R3-03), nor on an `/index.html`
    // address (#465) - see the GA4 effect above. Checked BEFORE the dedupe ref
    // is written, so the real route that follows still counts.
    if (!path || isTokenBearingPath(path) || isIndexHtmlPath(path)) return;
    if (lastRouteRef.current === path) return;
    lastRouteRef.current = path;

    analytics.trackEvent('app_route_viewed', {
      route: normalizeRoutePath(maskTokenPath(path)),
    });
  }, [location, user, loading]);

  // ---------------------------------------------------------------------------
  // First-party marketing-attribution page-view beacon (#100).
  //
  // Runs on route change for ANONYMOUS traffic only, INDEPENDENT of cookie
  // consent (this is not the GA4 surface — it is a first-party, no-identifier
  // count), and carries no user id. Authenticated navigation is skipped: it
  // would only add 'other'-bucket noise, burn the shared per-IP anonymous rate
  // bucket for office users, and write pointless rows. The effect WAITS for
  // the auth check to finish (`loading`) before deciding: firing while auth
  // is still resolving made every page refresh by a logged-in user emit one
  // beacon (user was still null), inflating the 'other' bucket by +1 per
  // refresh. An anonymous visitor's auth check resolves fast (401), so the
  // landing beacon still fires - just after resolution instead of before.
  // Debounced so the same path within 10s fires once. Fire-and-forget: never
  // blocks or delays navigation.
  // ---------------------------------------------------------------------------
  const beaconStateRef = useRef(null);
  useEffect(() => {
    // Wait until the auth check has resolved, then skip authenticated users.
    if (loading || user) return;
    // Not on a token-bearing path (#349 R3-03): the clean address the pay page
    // moves to is the landing that gets counted.
    if (isTokenBearingPath(location.pathname)) return;
    // Nor on an `/index.html` address (#465) - see the GA4 effect above.
    // Returning BEFORE the debounce keeps its state on the real `/<route>/`.
    if (isIndexHtmlPath(location.pathname)) return;

    // Masked BEFORE the debounce too, so the debounce state never holds the
    // concrete token path either.
    const path = maskTokenPath(location.pathname);
    const { fire, next } = decidePageViewBeacon(
      beaconStateRef.current,
      path,
      Date.now()
    );
    beaconStateRef.current = next;
    if (!fire) return;

    // referrer_domain only on the FIRST beacon of the session.
    let referrer;
    try {
      if (!sessionStorage.getItem('pv_ref_sent')) {
        sessionStorage.setItem('pv_ref_sent', '1');
        referrer = referrerDomain(document.referrer, window.location.hostname);
      }
    } catch (e) {
      /* storage unavailable — omit referrer */
    }

    let s;
    try {
      s = sessionStorage.getItem('acq_s') || undefined;
    } catch (e) {
      /* omit s */
    }

    analytics.sendPageView({ page_path: path, referrer_domain: referrer, s });
  }, [location, user, loading]);

  // ---------------------------------------------------------------------------
  // Anonymous page-engagement tracker (utils/engagementBeacon.js): visible time,
  // per-section time and max scroll depth, one cumulative snapshot per page
  // view. The SAME gating as the page-view beacon above - anonymous only, and
  // only after the auth check has resolved - and the same token-path rule.
  // The tracker itself measures public marketing pages only
  // (utils/engagementPages.js) and discards the view on any other path.
  //
  // Keyed on the PATHNAME, not the location: a query or hash change is the
  // same page view. NO cleanup function, on purpose: React cleanup must never
  // send (StrictMode would flush a one-tick view on every mount), and the
  // tracker's own start is idempotent per path. The sends happen on the
  // boundaries the tracker listens for itself, plus a new measured page (it
  // flushes the previous view). The auth flip (log-in / sign-up) and a
  // token-bearing path DISCARD the unsent view instead: nothing is sent at the
  // moment an account appears, so no beacon can be lined up with it by time.
  //
  // The consent check is handed over rather than imported there, so the
  // tracker never loads this service; it is consulted only while
  // ENGAGEMENT_REQUIRES_CONSENT is true.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (loading) return;
    if (user || isTokenBearingPath(location.pathname)) {
      discardEngagement();
      return;
    }
    // Not on a `/<route>/index.html` address (#465) - see the GA4 effect
    // above. Nothing starts there; the real `/<route>/` that replaces it a
    // moment later is the page view that gets measured.
    if (isIndexHtmlPath(location.pathname)) return;
    trackEngagementPage(maskTokenPath(location.pathname), {
      hasConsent: () => readStoredAnalyticsConsent() === true,
    });
  }, [location.pathname, user, loading]);

  // Set user ID when user logs in
  useEffect(() => {
    if (user && user.id && hasConsent) {
      // Hash the user ID for privacy
      const hashedId = sha256(user.id.toString()).toString();
      analytics.setUserId(hashedId);
    }
  }, [user, hasConsent]);

  // Track custom events
  const trackEvent = useCallback(
    (eventName, parameters) => {
      if (hasConsent) {
        analytics.trackEvent(eventName, parameters);
      }
    },
    [hasConsent]
  );

  // Track page views manually
  const trackPageView = useCallback(
    (path, title) => {
      if (hasConsent) {
        analytics.trackPageView(path, title);
      }
    },
    [hasConsent]
  );

  // Update consent and persist to localStorage
  const updateConsent = useCallback((analyticsConsent) => {
    setHasConsent(analyticsConsent);
    analytics.updateConsent(analyticsConsent);

    // Update localStorage with new consent.
    //
    // Written through `preferences.analytics` - the same key the cookie banner
    // writes and the shared reader reads. The old top-level `consent.analytics`
    // was the write half of the same bug: nothing has ever read it back, so a
    // consent set through this function was silently lost on the next load.
    // Everything else on the record (the other preference categories, version,
    // timestamp) is preserved - this function owns one field, not the object.
    const existingConsent = localStorage.getItem('cookieConsent');
    try {
      const consent = existingConsent ? JSON.parse(existingConsent) : {};
      consent.preferences = { ...(consent.preferences || {}), analytics: analyticsConsent };
      localStorage.setItem('cookieConsent', JSON.stringify(consent));
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  }, []);

  // Memoized (#464): this provider re-renders on every route and auth change,
  // and an inline value object re-rendered every useAnalytics() consumer each
  // time - including, on a prerendered page, the not-yet-hydrated route, where
  // a context change is exactly what makes React give up hydrating it. The
  // value now changes only when consent does. The callbacks above read
  // `hasConsent` through their deps, so each consumer still sees the current
  // consent; `updateConsent` reads no render state at all.
  const value = useMemo(
    () => ({
      analytics,
      trackEvent,
      trackPageView,
      updateConsent,
      hasConsent,
    }),
    [trackEvent, trackPageView, updateConsent, hasConsent]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsContext;